import { createMemo, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { cn } from "@/libs/cn";
import { MAX_SUBCATEGORIES } from "../../selector/sector-draft";
import type { SectorDraft, SlotIndex } from "../../selector/types";
import {
  categoryLabel,
  parentOf,
  subcategoryLabel,
} from "../../store/taxonomy-store";
import { FOCUS_RING, SuggestedMark } from "./primitives";

const I18N = "taxonomy.sector" as const;

/** A marker no label contains: the heading text is cut around the sector name. */
const LABEL_MARK = "\u0001";

/**
 * Jev's proposal next to the free slot of a profile that already has a sector:
 * «Aggiungi anche X», the subcategories it comes with, one button to take it.
 * With no sector at all the proposal is a tile of the grid instead (AC5).
 *
 * The button is not there once the proposal is taken, so the surface moves the
 * focus: `onAccepted` gets the slot the sector landed in.
 */
export const ProposalCard = (props: {
  draft: SectorDraft;
  onAccepted?: (index: SlotIndex) => void;
  class?: string;
}) => {
  const { t } = useI18n();
  const proposal = createMemo(() =>
    props.draft.chosenCategories().length > 0
      ? props.draft.visibleProposal()
      : null,
  );

  const accept = () => {
    const index = props.draft.freeIndex();
    props.draft.acceptProposal();
    if (index !== null) props.onAccepted?.(index);
  };

  return (
    <Show when={proposal()}>
      {(current) => {
        /** The subcategories the draft will take: the sector's own, at most 3. */
        const subs = () =>
          current()
            .subcategories.filter((key) => parentOf(key) === current().category)
            .slice(0, MAX_SUBCATEGORIES);
        const heading = () =>
          t(`${I18N}.addAlso`, { label: LABEL_MARK }).split(LABEL_MARK);
        return (
          <div
            class={cn(
              "flex flex-col gap-2 rounded-lg border border-dashed border-ring bg-primary/5 p-3",
              props.class,
            )}
          >
            <div class="flex items-start gap-2">
              <SuggestedMark class="mt-1 text-ring" />
              <div class="min-w-0 flex-1">
                <p class="text-sm text-foreground">
                  {heading()[0]}
                  <strong class="font-semibold">
                    {categoryLabel(current().category)}
                  </strong>
                  {heading()[1]}
                </p>
                <Show when={subs().length > 0}>
                  <ul
                    class="mt-1.5 flex flex-wrap gap-1.5"
                    aria-label={t(`${I18N}.proposedSubcategories`)}
                  >
                    <For each={subs()}>
                      {(key) => (
                        <li class="inline-flex items-center rounded-md border border-dashed border-ring bg-white px-2 py-0.5 text-xs font-semibold text-foreground">
                          {subcategoryLabel(key)}
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>
              </div>
            </div>
            <div class="flex justify-end">
              <Button
                size="sm"
                variant="outline"
                class={cn("bg-white text-ring", FOCUS_RING)}
                onClick={accept}
              >
                <i class="ph ph-plus mr-1" aria-hidden="true" />
                {t(`${I18N}.addAsSecondary`)}
              </Button>
            </div>
          </div>
        );
      }}
    </Show>
  );
};
